/**
 * Network and VPN diagnostics: the API server's interfaces, which ones are VPNs,
 * and whether the CORS settings admit them.
 *
 * Built for one question that otherwise needs a shell on the box: "an operator
 * on the tailnet gets a CORS error, is this server even on the VPN, and does
 * CORS_VPN_CIDRS cover it?" Read-only. Nothing here changes a setting; the
 * answer to a wrong one is an edit to the deployment's .env.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { getNetworkInfo, type NetworkInfo, type NetworkInterface } from '@/api/network';

const MUTED = 'rgba(244,244,245,0.55)';
const FAINT = 'rgba(244,244,245,0.4)';
const OK = 'oklch(0.78 0.15 155)';
const CAUTION = 'oklch(0.82 0.15 85)';
const DANGER_FG = 'oklch(0.72 0.19 28)';

const PROVIDER_LABEL: Record<string, string> = {
  tailscale: 'Tailscale',
  netbird: 'Netbird',
  zerotier: 'ZeroTier',
  wireguard: 'WireGuard',
  tailscale_or_netbird: 'Tailscale or Netbird',
};

function providerLabel(iface: NetworkInterface): string {
  if (!iface.vpnProvider) return '';
  const name = PROVIDER_LABEL[iface.vpnProvider] ?? iface.vpnProvider;
  return iface.vpnEvidence === 'address' ? `${name}, by address` : name;
}

function Heading({ children }: { children: string }) {
  return <div className="mb-2 label-mono text-[10px]">{children}</div>;
}

export function NetworkTab() {
  const [info, setInfo] = useState<NetworkInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void getNetworkInfo().then((result) => {
      if (result.ok) setInfo(result.data);
      else setError(result.error.message);
    });
  }, []);

  if (error) {
    return (
      <div className="p-[18px] label-mono text-[10px]" style={{ color: DANGER_FG }}>
        {error}
      </div>
    );
  }
  if (!info) {
    return (
      <div className="p-[18px] text-[11px]" style={{ color: MUTED }}>
        Reading interfaces…
      </div>
    );
  }

  const vpns = info.interfaces.filter((i) => i.vpnProvider);

  return (
    <div className="flex flex-col gap-5 p-[18px]">
      <section>
        <div className="mb-2 flex flex-wrap items-baseline gap-2">
          <span className="label-mono text-[10px]">NETWORK</span>
          <span className="label-mono text-[10px]" style={{ color: MUTED }}>
            {info.hostname}
          </span>
          <span
            className="ml-auto label-mono text-[9px]"
            style={{ color: info.vpn ? OK : CAUTION }}
          >
            {info.vpn
              ? `VPN ${info.vpn.address ?? ''} ON ${info.vpn.interface}`
              : 'NO VPN INTERFACE VISIBLE'}
          </span>
        </div>
        {info.inContainer ? (
          <p className="mb-3 text-[11px]" style={{ color: CAUTION }}>
            The API runs in a container with its own network, so these are the container&apos;s
            interfaces. A VPN on the host does not appear here even when it is up; check it on the
            host with <span className="label-mono">tailscale status</span> or{' '}
            <span className="label-mono">netbird status</span>.
          </p>
        ) : null}
        <div className="flex flex-col gap-[3px] label-mono text-[11px]">
          {info.interfaces.map((iface) => (
            <div
              key={iface.name}
              className="flex flex-wrap items-baseline gap-3 rounded-[11px] px-3 py-1.5"
              style={{ background: 'rgba(255,255,255,0.03)' }}
            >
              <span className="w-[110px] shrink-0 truncate">{iface.name}</span>
              <span className="min-w-0 flex-1 basis-[160px] [overflow-wrap:anywhere]" style={{ color: 'rgba(244,244,245,0.85)' }}>
                {iface.addresses.join(', ')}
              </span>
              {iface.vpnProvider ? <span style={{ color: OK }}>{providerLabel(iface)}</span> : null}
            </div>
          ))}
          {info.interfaces.length === 0 ? (
            <p className="px-3 py-4 text-[11px]" style={{ color: MUTED }}>
              No interfaces with an address were found.
            </p>
          ) : null}
        </div>
      </section>

      <section>
        <Heading>CORS</Heading>
        <div className="flex flex-col gap-[3px] label-mono text-[11px]">
          <Row label="VPN ranges">
            {info.cors.vpnCidrs.length ? info.cors.vpnCidrs.join(', ') : 'none (CORS_VPN_CIDRS)'}
          </Row>
          {vpns.map((iface) => {
            const covered = iface.corsAdmitted.length > 0;
            return (
              <Row key={iface.name} label={`${iface.name} admitted`}>
                <span style={{ color: covered ? OK : CAUTION }}>
                  {covered
                    ? iface.corsAdmitted.join(', ')
                    : 'no, its addresses are outside CORS_VPN_CIDRS'}
                </span>
              </Row>
            );
          })}
          {info.cors.publicOrigins.map((entry) => (
            <Row key={entry.origin} label="public origin">
              {entry.origin}
              <span style={{ color: MUTED }}>
                {entry.vpnInterface ? `  via ${entry.vpnInterface}` : '  not on a VPN seen here'}
              </span>
            </Row>
          ))}
          <Row label="exact origins">
            {info.cors.origins.length ? info.cors.origins.join(', ') : 'none (CORS_ORIGINS)'}
          </Row>
        </div>
        <p className="mt-2.5 text-[10px]" style={{ color: FAINT }}>
          Not needed when the UI is opened from this server&apos;s own address, which is same-origin.
          CORS matters when the UI is served from somewhere else, such as a dev server on a laptop
          on the VPN. Settings live in the deployment&apos;s .env.
        </p>
      </section>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div
      className="flex flex-wrap items-baseline gap-3 rounded-[11px] px-3 py-1.5"
      style={{ background: 'rgba(255,255,255,0.03)' }}
    >
      <span className="w-[150px] shrink-0" style={{ color: MUTED }}>
        {label}
      </span>
      <span className="min-w-0 flex-1 basis-[180px] [overflow-wrap:anywhere]">{children}</span>
    </div>
  );
}
